import { observable, action, makeObservable } from 'mobx';
import { RootStore } from './root';
import * as yup from 'yup';
import { ParsedStore, ImportWarnings } from '../helpers/store-helper';
export type { ImportWarnings };
import { NotificationManager } from '../components/notification-manager';
import {
    HttpMethod,
    PaginatedList,
    StoreChangeHistory,
    UpsertStoresResponse,
} from '../types';
import { StoreBrand } from './constants';

export type CoordSuspects = Map<string, { lat?: number; lng?: number }>;

export {
    StoreBrand,
    STORE_BRAND_LABELS,
    APPLICATION_TO_BRAND,
    BRAND_CSV_FILENAMES,
} from './constants';

const StoreSchema = yup.object({
    id: yup.number().required(),
    nref: yup.string().required(),
    title: yup.string().notRequired().nullable(),
    lat: yup.number().notRequired().nullable(),
    lng: yup.number().notRequired().nullable(),
    img: yup.string().notRequired().nullable(),
    tel: yup.string().notRequired().nullable(),
    hours: yup.array().of(yup.string().nullable()).required(),
    services: yup.array(yup.string().required()).required(),
    status: yup.string().required(),
    carWashStatus: yup.string().required(),
    carWashClosedOn: yup.string().notRequired().nullable(),
    brand: yup
        .string()
        .oneOf([StoreBrand.COUCHE_TARD, StoreBrand.CIRCLE_K])
        .notRequired()
        .nullable(),
    addr: yup
        .object({
            street: yup.string().notRequired().nullable(),
            city: yup.string().notRequired().nullable(),
            post: yup.string().notRequired().nullable(),
        })
        .required(),
});

const FetchStoresSchema = yup.object({
    stores: yup.array(StoreSchema).required(),
});

export type Store = yup.InferType<typeof StoreSchema>;

const UpsertStoresResponseSchema = yup.object({
    applied: yup
        .object({
            added: yup.number().required(),
            modified: yup.number().required(),
        })
        .required(),
    skipped: yup
        .array(
            yup.object({
                id: yup.number().nullable().notRequired(),
                nref: yup.string().nullable().notRequired(),
                reason: yup
                    .string()
                    .oneOf(['brand-mismatch', 'not-found'])
                    .required(),
                existingBrand: yup.string().notRequired().nullable(),
            })
        )
        .required(),
});

export class StoresStore {
    @observable stores: Store[] | null = null;
    @observable parsedStore: ParsedStore[] | null = null;
    @observable originalParsedStore: ParsedStore[] | null = null;
    @observable coordSuspects: CoordSuspects = new Map();
    @observable importWarnings: ImportWarnings | null = null;
    @observable isFetchingStores = false;
    @observable isUpdatingStores = false;
    @observable isDeletingStores = false;
    @observable storeHistory: PaginatedList<StoreChangeHistory> | null = null;
    @observable isFetchingHistory = false;

    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        makeObservable(this);
        this.rootStore = rootStore;
    }

    @action
    setParsedStore(parsedStore: ParsedStore[]) {
        this.parsedStore = parsedStore;
    }

    @action
    setCoordImportState(original: ParsedStore[], suspects: CoordSuspects) {
        this.originalParsedStore = original;
        this.coordSuspects = suspects;
    }

    @action
    clearCoordImportState() {
        this.originalParsedStore = null;
        this.coordSuspects = new Map();
    }

    @action
    setImportWarnings(warnings: ImportWarnings | null) {
        this.importWarnings = warnings;
    }

    @action
    async uploadChanges(payload: {
        brand: StoreBrand;
        additions: Store[];
        modifications: Store[];
    }): Promise<UpsertStoresResponse | null> {
        if (this.isUpdatingStores) return null;

        this.isUpdatingStores = true;
        try {
            const response = await this.rootStore.makeNetworkCall(
                { method: HttpMethod.PUT, url: '/stores', data: payload },
                UpsertStoresResponseSchema
            );
            if (!response.err && response.data) {
                return response.data as UpsertStoresResponse;
            }
            return null;
        } finally {
            this.isUpdatingStores = false;
        }
    }

    // `null` = no pending fetch; `undefined` = pending brand-less fetch;
    // `StoreBrand` = pending fetch for that brand.
    @observable private pendingBrand: StoreBrand | undefined | null = null;

    @action
    async fetchStores(this: StoresStore, brand?: StoreBrand) {
        if (this.isFetchingStores) {
            this.pendingBrand = brand;
            return;
        }

        this.isFetchingStores = true;
        this.pendingBrand = null;
        try {
            const response = await this.rootStore.makeNetworkCall(
                {
                    method: HttpMethod.GET,
                    url: '/stores',
                    params: brand ? { brand } : undefined,
                },
                FetchStoresSchema
            );

            if (!response.err) {
                this.stores = response.data.stores;
            }

            if (this.pendingBrand !== null) {
                const nextBrand = this.pendingBrand;
                this.pendingBrand = null;
                await this.fetchStores(nextBrand);
            }
        } catch (error) {
            NotificationManager.showError('Failed to fetch stores');
        } finally {
            this.isFetchingStores = false;
        }
    }

    @action
    async deleteStores(payload: { nrefs: string[] }) {
        if (this.isDeletingStores) {
            return 0;
        }

        this.isDeletingStores = true;
        const response = await this.rootStore.makeNetworkCall<{
            deletedCount: number;
        }>({
            method: HttpMethod.DELETE,
            url: '/stores',
            data: payload,
        });
        this.isDeletingStores = false;

        if (!response.err && response.data) {
            return response.data.deletedCount;
        }

        return 0;
    }

    @action
    async fetchStoreHistory(page: number, perPage: number) {
        if (this.isFetchingHistory) {
            return;
        }

        this.isFetchingHistory = true;
        const response = await this.rootStore.makeNetworkCall<
            PaginatedList<StoreChangeHistory>
        >({
            method: HttpMethod.GET,
            url: '/stores/history',
            params: { page, perPage },
        });
        this.isFetchingHistory = false;

        if (!response.err && response.data) {
            this.storeHistory = response.data;
        }
    }
}
